import { forwardRef, useEffect, useId, useMemo, useState, type ReactNode } from 'react';
import { Check, ChevronDown, ExternalLink, Plus, Search, X } from 'lucide-react';
import { cn } from '@/shared/lib/cn';
import { foldSearch } from '@/shared/lib/foldSearch';
import { Badge } from '@/shared/ui/core/Badge';
import { Button } from '@/shared/ui/core/Button';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/shared/ui/core/Popover';
import {
  controlHeightClass,
  errorClass,
  fieldBorderClass,
  fieldFocusClass,
  hintClass,
  labelClass,
} from '@/shared/ui/core/styles';
import type { ControlSize, FieldTone } from '@/shared/ui/core/types';
import type { SelectOption } from '@/shared/ui/core/Select';
import { useFormFieldContext } from '@/shared/ui/core/formFieldContext';

export type SearchableSelectOption = SelectOption;

export type SearchableSelectProps = {
  label?: string;
  required?: boolean;
  hint?: string;
  error?: string;
  tone?: FieldTone;
  size?: ControlSize;
  options: SearchableSelectOption[];
  placeholder?: string;
  searchPlaceholder?: string;
  emptyText?: string;
  value?: string;
  onValueChange?: (value: string) => void;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  disabled?: boolean;
  id?: string;
  className?: string;
  rootClassName?: string;
  'aria-label'?: string;
  /**
   * Action button pinned at the bottom of the dropdown list.
   * Always visible when `onCreateNew` is provided.
   */
  createLabel?: string;
  onCreateNew?: (query: string) => void;
  /** Link navigation URL if action opens an external or management page */
  createHref?: string;
  createTarget?: '_blank' | '_self';
  /** Options already come from the server for the current query. */
  remote?: boolean;
  /** Show a control that clears the current value. */
  clearable?: boolean;
  onSearchChange?: (query: string) => void;
  /** Pencil on the first line of a dropdown row. Does not select the row. */
  onEditOption?: (value: string) => void;
  /**
   * Whether to show option description in the select trigger button.
   * If false, only `label` and `badge` are shown in the trigger, keeping single-line height.
   * Defaults to true.
   */
  triggerDescription?: boolean;
};

function EditLabel({ onEdit }: { onEdit: () => void }) {
  return (
    <span
      role="button"
      tabIndex={0}
      className="shrink-0 text-[11px] font-medium text-primary hover:underline"
      onPointerDown={(event) => event.stopPropagation()}
      onClick={(event) => {
        event.preventDefault();
        event.stopPropagation();
        onEdit();
      }}
      onKeyDown={(event) => {
        if (event.key !== 'Enter' && event.key !== ' ') return;
        event.preventDefault();
        event.stopPropagation();
        onEdit();
      }}
    >
      Chỉnh sửa
    </span>
  );
}

function optionDetailLines(opt: SearchableSelectOption): string[] {
  const lines: string[] = [];
  if (opt.description?.trim()) lines.push(opt.description.trim());
  for (const line of opt.descriptions ?? []) {
    const text = line.trim();
    if (text) lines.push(text);
  }
  return lines;
}

function OptionLines({
  opt,
  afterLabel,
}: {
  opt: SearchableSelectOption;
  afterLabel?: ReactNode;
}) {
  const badge = opt.badge ? (
    <Badge
      tone={opt.badgeTone ?? 'info'}
      label={opt.badge}
      className="px-1.5 py-0 text-[10px] leading-4"
    />
  ) : null;
  const detailLines = optionDetailLines(opt);
  if (detailLines.length === 0 && !badge && !afterLabel) {
    return <span className="min-w-0 flex-1 truncate">{opt.label}</span>;
  }
  return (
    <span className="min-w-0 flex-1">
      <span className="flex min-w-0 items-center gap-1">
        <span className="truncate">{opt.label}</span>
        {badge}
        {afterLabel}
      </span>
      {detailLines.map((line) => (
        <span key={line} className="block truncate text-[11px] leading-4 text-text-secondary">
          {line}
        </span>
      ))}
    </span>
  );
}

function optionTitle(opt: SearchableSelectOption): string {
  const details = optionDetailLines(opt);
  return details.length > 0 ? `${opt.label} · ${details.join(' · ')}` : opt.label;
}

function matchesQuery(opt: SearchableSelectOption, q: string): boolean {
  if (!q) return true;
  const hay = foldSearch(
    `${opt.label} ${opt.badge ?? ''} ${optionDetailLines(opt).join(' ')} ${opt.value}`,
  );
  return hay.includes(foldSearch(q));
}

export const SearchableSelect = forwardRef<HTMLButtonElement, SearchableSelectProps>(
  function SearchableSelect(
    {
      label,
      required = false,
      hint,
      error,
      tone,
      size = 'md',
      options,
      placeholder = 'Chọn…',
      searchPlaceholder = 'Tìm…',
      emptyText = 'Không có kết quả',
      value,
      onValueChange,
      open: controlledOpen,
      onOpenChange: controlledOnOpenChange,
      disabled,
      id,
      className,
      rootClassName,
      'aria-label': ariaLabel,
      createLabel = 'Thêm mới',
      onCreateNew,
      createHref,
      createTarget = '_blank',
      remote = false,
      clearable = false,
      onSearchChange,
      onEditOption,
      triggerDescription = true,
    },
    ref,
  ) {
    const autoId = useId();
    const fieldId = id ?? autoId;
    const listId = `${fieldId}-listbox`;
    const formField = useFormFieldContext();
    const effectiveError = error || (formField.inFormField ? formField.error : undefined);
    const describedBy = effectiveError ? `${fieldId}-error` : hint ? `${fieldId}-hint` : undefined;
    const resolvedTone: FieldTone = tone ?? (effectiveError ? 'error' : 'default');
    const shouldRenderError = Boolean(effectiveError && (!formField.inFormField || (!formField.error && Boolean(error))));

    const isControlled = controlledOpen !== undefined;
    const [internalOpen, setInternalOpen] = useState(false);
    const open = isControlled ? controlledOpen : internalOpen;

    const [query, setQuery] = useState('');

    // Global coordinator: when ANY SearchableSelect opens, close all other open selects
    useEffect(() => {
      const handler = (e: Event) => {
        const customEvent = e as CustomEvent<{ id: string }>;
        if (customEvent.detail?.id !== fieldId) {
          if (isControlled) {
            controlledOnOpenChange?.(false);
          } else {
            setInternalOpen(false);
          }
          setQuery('');
        }
      };
      window.addEventListener('tm-select-open', handler);
      return () => window.removeEventListener('tm-select-open', handler);
    }, [fieldId, isControlled, controlledOnOpenChange]);

    const selected = options.find((o) => o.value === value);
    const editSelected = () => {
      if (!selected || !onEditOption || selected.canEdit === false) return;
      handleOpenChange(false);
      onEditOption(selected.value);
    };
    const showSelectedEdit = Boolean(onEditOption) && selected?.canEdit !== false;
    const stacked =
      triggerDescription && Boolean(selected && optionDetailLines(selected).length > 0);
    const normalizedQuery = query.trim().toLowerCase();
    const filtered = useMemo(
      () => (remote ? options : options.filter((o) => matchesQuery(o, normalizedQuery))),
      [options, normalizedQuery, remote],
    );
    const showCreate = Boolean(onCreateNew || createHref);

    const handleOpenChange = (next: boolean) => {
      if (next) {
        window.dispatchEvent(new CustomEvent('tm-select-open', { detail: { id: fieldId } }));
      }
      if (isControlled) {
        controlledOnOpenChange?.(next);
      } else {
        setInternalOpen(next);
      }
      if (!next) {
        setQuery('');
        onSearchChange?.('');
      }
    };

    // Robust click-outside listener on capture phase:
    // Ensures dropdown ALWAYS closes when clicking outside, regardless of any stopPropagation in modals
    useEffect(() => {
      if (!open) return;

      const handleDocumentPointerDown = (event: PointerEvent) => {
        const target = event.target as Node | null;
        if (!target) return;

        // If clicked inside the trigger button, let PopoverTrigger handle it
        const triggerEl = document.getElementById(fieldId);
        if (triggerEl && triggerEl.contains(target)) return;

        // Search field, list, and create action all live in the popper panel.
        const element = target instanceof Element ? target : target.parentElement;
        if (element?.closest('[data-radix-popper-content-wrapper]')) return;

        // Clicked outside! Close immediately
        handleOpenChange(false);
      };

      document.addEventListener('pointerdown', handleDocumentPointerDown, true);
      return () => {
        document.removeEventListener('pointerdown', handleDocumentPointerDown, true);
      };
    }, [open, fieldId]);

    const pick = (next: string) => {
      onValueChange?.(next);
      handleOpenChange(false);
    };

    const create = () => {
      const q = query.trim();
      handleOpenChange(false);
      if (onCreateNew) {
        onCreateNew(q);
      } else if (createHref) {
        if (createTarget === '_blank') {
          window.open(createHref, '_blank', 'noopener,noreferrer');
        } else {
          window.location.href = createHref;
        }
      }
    };

    return (
      <div className={cn('flex w-full flex-col gap-1.5', rootClassName)}>
        {label ? (
          <label htmlFor={fieldId} className={cn(labelClass, 'flex items-center gap-1')}>
            <span>{label}</span>
            {required ? (
              <span className="text-danger font-bold text-sm leading-none" aria-hidden="true">
                *
              </span>
            ) : null}
          </label>
        ) : null}
        <Popover open={open} onOpenChange={handleOpenChange}>
          <PopoverTrigger asChild>
            <button
              ref={ref}
              type="button"
              id={fieldId}
              disabled={disabled}
              aria-label={ariaLabel}
              aria-invalid={resolvedTone === 'error' || undefined}
              aria-describedby={describedBy}
              aria-haspopup="listbox"
              aria-expanded={open}
              aria-controls={listId}
              title={selected ? optionTitle(selected) : undefined}
              className={cn(
                'inline-flex w-full min-w-0 items-center justify-between rounded-input border bg-surface cursor-pointer overflow-hidden',
                size === 'sm' ? 'px-2 gap-1 text-caption' : 'px-3 gap-2 text-body',
                'text-left text-text outline-none transition-colors',
                fieldFocusClass,
                'disabled:cursor-not-allowed disabled:bg-disabled-bg disabled:text-disabled-fg',
                !selected && 'text-text-muted',
                stacked
                  ? optionDetailLines(selected!).length > 1
                    ? 'min-h-14 py-1.5'
                    : 'min-h-12 py-1.5'
                  : controlHeightClass[size],
                fieldBorderClass[resolvedTone],
                className,
              )}
            >
              {selected ? (
                triggerDescription ? (
                  <OptionLines
                    opt={selected}
                    afterLabel={showSelectedEdit ? <EditLabel onEdit={editSelected} /> : null}
                  />
                ) : (
                  <span className="flex min-w-0 flex-1 items-center gap-1.5">
                    <span className="truncate">{selected.label}</span>
                    {selected.badge ? (
                      <Badge
                        tone={selected.badgeTone ?? 'info'}
                        label={selected.badge}
                        className="px-1.5 py-0 text-[10px] leading-4"
                      />
                    ) : null}
                    {showSelectedEdit ? <EditLabel onEdit={editSelected} /> : null}
                  </span>
                )
              ) : (
                <span className="min-w-0 truncate">{placeholder}</span>
              )}
              {clearable && selected ? (
                <span
                  role="button"
                  tabIndex={-1}
                  aria-label="Bỏ chọn"
                  className="inline-flex size-5 shrink-0 items-center justify-center rounded text-text-secondary hover:bg-surface-muted hover:text-text"
                  onPointerDown={(event) => {
                    event.preventDefault();
                    event.stopPropagation();
                  }}
                  onClick={(event) => {
                    event.preventDefault();
                    event.stopPropagation();
                    onValueChange?.('');
                  }}
                >
                  <X className="size-3.5" aria-hidden />
                </span>
              ) : (
                <ChevronDown className="size-4 shrink-0 text-text-secondary" aria-hidden />
              )}
            </button>
          </PopoverTrigger>
          <PopoverContent
            className="w-[var(--radix-popover-trigger-width)] p-0"
            onOpenAutoFocus={(e) => {
              e.preventDefault();
              const el = document.getElementById(`${fieldId}-search`);
              el?.focus();
            }}
          >
            <div className="border-b border-border p-2">
              <label className="relative flex items-center">
                <Search
                  className="pointer-events-none absolute left-2.5 size-3.5 text-text-muted"
                  aria-hidden
                />
                <input
                  id={`${fieldId}-search`}
                  type="text"
                  inputMode="search"
                  value={query}
                  onChange={(e) => {
                    setQuery(e.target.value);
                    onSearchChange?.(e.target.value);
                  }}
                  placeholder={searchPlaceholder}
                  autoComplete="off"
                  className={cn(
                    'w-full rounded-input border border-border bg-surface py-1.5 pr-3 pl-6 text-[12px] text-text',
                    'placeholder:text-text-muted outline-none',
                    fieldFocusClass,
                  )}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      const only = filtered.length === 1 ? filtered[0] : undefined;
                      if (only && !only.disabled) {
                        pick(only.value);
                      } else if (showCreate && filtered.length === 0) {
                        create();
                      }
                    }
                  }}
                />
              </label>
            </div>
            <div
              id={listId}
              role="listbox"
              aria-label={ariaLabel ?? label ?? placeholder}
              className="max-h-60 overflow-y-auto p-1"
            >
              {filtered.length === 0 ? (
                <p className="px-3 py-2 text-caption text-text-secondary m-0">{emptyText}</p>
              ) : (
                filtered.map((opt) => {
                  const isSelected = opt.value === value;
                  const showEdit = Boolean(onEditOption) && opt.canEdit !== false;
                  return (
                    <div
                      key={opt.value}
                      role="option"
                      aria-selected={isSelected}
                      aria-disabled={opt.disabled || undefined}
                      title={optionTitle(opt)}
                      className={cn(
                        'flex cursor-pointer select-none items-center justify-between gap-1.5 rounded-control py-1.5 px-2.5 text-left text-[12px] text-text',
                        'hover:bg-primary-soft hover:text-primary',
                        opt.disabled && 'pointer-events-none text-text-disabled',
                        isSelected && 'bg-primary-soft text-primary font-medium',
                      )}
                      onClick={() => {
                        if (!opt.disabled) pick(opt.value);
                      }}
                    >
                      <OptionLines
                        opt={opt}
                        afterLabel={
                          showEdit ? (
                            <EditLabel
                              onEdit={() => {
                                handleOpenChange(false);
                                onEditOption?.(opt.value);
                              }}
                            />
                          ) : null
                        }
                      />
                      {isSelected ? (
                        <Check className="size-3.5 shrink-0 text-primary" aria-hidden />
                      ) : null}
                    </div>
                  );
                })
              )}
            </div>
            {showCreate ? (
              <div className="border-t border-border p-2">
                <Button
                  type="button"
                  variant="soft"
                  size="sm"
                  fullWidth
                  leftIcon={
                    createHref && !onCreateNew ? (
                      <ExternalLink className="size-3.5" aria-hidden />
                    ) : (
                      <Plus className="size-4" aria-hidden />
                    )
                  }
                  onClick={create}
                >
                  {createLabel}
                </Button>
              </div>
            ) : null}
          </PopoverContent>
        </Popover>
        {shouldRenderError ? (
          <p id={`${fieldId}-error`} className={errorClass} role="alert">
            {effectiveError}
          </p>
        ) : hint ? (
          <p id={`${fieldId}-hint`} className={hintClass}>
            {hint}
          </p>
        ) : null}
      </div>
    );
  },
);
